"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ChevronsUpDown, FileText, Globe, LayoutDashboard, LogOut, Monitor, Moon, Sun, UserIcon } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useAuth, signOut } from "./AuthProvider";
import { BudgetCue } from "./BudgetCue";
import { isSuperAdmin } from "@/lib/auth/user";
import { GLOBAL_ROUTES } from "@/lib/navigation/portal-routes";
import { cn, getFirstName, getUserInitials } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { setUserLocale } from "@/app/actions/locale-actions";
import { LOCALE_LABELS, SUPPORTED_LOCALES } from "@/i18n/config";

/** Avatar with the one-pager's burgundy fallback. */
function UserAvatar(t0) {
  const $ = _c(14);
  const {
    user,
    className,
    fallbackClassName
  } = t0;
  const t1 = user.photoURL || undefined;
  const t2 = user.displayName || "User";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <AvatarImage src={t1} alt={t2} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== fallbackClassName) {
    t4 = cn("bg-[#6c2c59] font-medium text-[#f7f7f7]", fallbackClassName);
    $[3] = fallbackClassName;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== user.displayName) {
    t5 = getUserInitials(user.displayName);
    $[5] = user.displayName;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <AvatarFallback className={t4}>{t5}</AvatarFallback>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== className || $[11] !== t3 || $[12] !== t6) {
    t7 = <Avatar className={className}>{t3}{t6}</Avatar>;
    $[10] = className;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

/**
 * Bottom-left account menu for the app sidebar (Claude/ChatGPT style).
 * Replaces the old top-bar user dropdown and folds the Documents link and the
 * light/dark theme control into a single popover anchored to the sidebar footer.
 */
export function SidebarUserMenu(t0) {
  const $ = _c(99);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "sidebar" : t1;
  const {
    user
  } = useAuth();
  const tNav = useTranslations("nav");
  const tMenu = useTranslations("userMenu");
  const locale = useLocale();
  const {
    setTheme
  } = useTheme();
  if (!user) {
    return null;
  }
  let t2;
  if ($[0] !== user || $[1] !== variant) {
    t2 = <DropdownMenuTrigger asChild={true}>{variant === "header" ? <button type="button" aria-label="User menu" className="rounded-full ring-offset-background transition-shadow hover:ring-2 hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><UserAvatar user={user} className="size-8" fallbackClassName="text-xs" /></button> : <SidebarMenuButton size="lg" aria-label="User menu" className="h-11 rounded-full border border-white/60 bg-white/45 px-1.5 text-[#493e46] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] backdrop-blur-xl hover:bg-white/60 hover:text-[#332b32] data-[state=open]:bg-white/65 data-[state=open]:text-[#332b32] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#e9e1e6] dark:hover:bg-white/[0.10] dark:hover:text-[#f7f3f0] dark:data-[state=open]:bg-white/[0.12] dark:data-[state=open]:text-[#f7f3f0]"><UserAvatar user={user} className="size-8 shrink-0" fallbackClassName="text-xs" /><div className="grid flex-1 text-left text-sm leading-tight"><span className="truncate font-medium">{getFirstName(user.displayName)}</span><span className="truncate text-xs text-muted-foreground">{user.email}</span></div><ChevronsUpDown className="ml-auto size-4" /></SidebarMenuButton>}</DropdownMenuTrigger>;
    $[0] = user;
    $[1] = variant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = variant === "header" ? "bottom" : "top";
  const t4 = variant === "header" ? "end" : "start";
  const t5 = variant === "sidebar" && "w-[var(--radix-dropdown-menu-trigger-width)]";
  let t6;
  if ($[3] !== t5) {
    t6 = cn(t5, "min-w-64 p-0");
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== user) {
    t7 = <UserAvatar user={user} className="size-10 shrink-0" fallbackClassName="text-sm" />;
    $[5] = user;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== tMenu || $[8] !== user.displayName) {
    t8 = tMenu("greeting", {
      name: getFirstName(user.displayName)
    });
    $[7] = tMenu;
    $[8] = user.displayName;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <span className="truncate text-sm font-medium text-foreground">{t8}</span>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = user.email || undefined;
  let t11;
  if ($[12] !== t10 || $[13] !== user.email) {
    t11 = <span className="break-all text-xs text-muted-foreground" title={t10}>{user.email}</span>;
    $[12] = t10;
    $[13] = user.email;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t9}{t11}</div>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t7) {
    t13 = <div className="flex items-center gap-3 px-4 py-4">{t7}{t12}</div>;
    $[18] = t12;
    $[19] = t7;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BudgetCue />;
    t15 = <DropdownMenuSeparator className="my-0" />;
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <UserIcon className="size-4" />;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== tMenu) {
    t17 = tMenu("profile");
    $[24] = tMenu;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17) {
    t18 = <DropdownMenuItem asChild={true} className="gap-3 px-3 py-2.5"><Link href="/profile">{t16}{t17}</Link></DropdownMenuItem>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== tMenu || $[29] !== user) {
    t19 = isSuperAdmin(user) && <DropdownMenuItem asChild={true} className="gap-3 px-3 py-2.5"><Link href={GLOBAL_ROUTES.platformAdmin} data-testid="user-menu-platform-admin"><LayoutDashboard className="size-4" />{tMenu("platformAdmin")}</Link></DropdownMenuItem>;
    $[28] = tMenu;
    $[29] = user;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FileText className="size-4" />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== tMenu) {
    t21 = tMenu("terms");
    $[32] = tMenu;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t21) {
    t22 = <DropdownMenuItem asChild={true} className="gap-3 px-3 py-2.5"><Link href="/legal/terms">{t20}{t21}</Link></DropdownMenuItem>;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Sun className="size-4 text-muted-foreground dark:hidden" />;
    t24 = <Moon className="hidden size-4 text-muted-foreground dark:block" />;
    $[36] = t23;
    $[37] = t24;
  } else {
    t23 = $[36];
    t24 = $[37];
  }
  let t25;
  if ($[38] !== tNav) {
    t25 = tNav("theme");
    $[38] = tNav;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t25) {
    t26 = <DropdownMenuSubTrigger className="gap-3 px-3 py-2.5">{t23}{t24}{t25}</DropdownMenuSubTrigger>;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== setTheme) {
    t27 = () => setTheme("light");
    $[42] = setTheme;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Sun className="size-4" />;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t27) {
    t29 = <DropdownMenuItem onClick={t27} className="gap-2">{t28} Light</DropdownMenuItem>;
    $[45] = t27;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== setTheme) {
    t30 = () => setTheme("dark");
    $[47] = setTheme;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Moon className="size-4" />;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t30) {
    t32 = <DropdownMenuItem onClick={t30} className="gap-2">{t31} Dark</DropdownMenuItem>;
    $[50] = t30;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== setTheme) {
    t33 = () => setTheme("system");
    $[52] = setTheme;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Monitor className="size-4" />;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== t33) {
    t35 = <DropdownMenuItem onClick={t33} className="gap-2">{t34} System</DropdownMenuItem>;
    $[55] = t33;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] !== t29 || $[58] !== t32 || $[59] !== t35) {
    t36 = <DropdownMenuSubContent>{t29}{t32}{t35}</DropdownMenuSubContent>;
    $[57] = t29;
    $[58] = t32;
    $[59] = t35;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] !== t26 || $[62] !== t36) {
    t37 = <DropdownMenuSub>{t26}{t36}</DropdownMenuSub>;
    $[61] = t26;
    $[62] = t36;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Globe className="size-4 text-muted-foreground" />;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] !== tMenu) {
    t39 = tMenu("language");
    $[65] = tMenu;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== t39) {
    t40 = <DropdownMenuSubTrigger className="gap-3 px-3 py-2.5">{t38}{t39}</DropdownMenuSubTrigger>;
    $[67] = t39;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = SUPPORTED_LOCALES.map(_temp2);
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== locale) {
    t42 = <DropdownMenuSubContent><DropdownMenuRadioGroup value={locale} onValueChange={_temp}>{t41}</DropdownMenuRadioGroup></DropdownMenuSubContent>;
    $[70] = locale;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== t40 || $[73] !== t42) {
    t43 = <DropdownMenuSub>{t40}{t42}</DropdownMenuSub>;
    $[72] = t40;
    $[73] = t42;
    $[74] = t43;
  } else {
    t43 = $[74];
  }
  let t44;
  if ($[75] !== t18 || $[76] !== t19 || $[77] !== t22 || $[78] !== t37 || $[79] !== t43) {
    t44 = <div className="p-1">{t18}{t19}{t22}{t37}{t43}</div>;
    $[75] = t18;
    $[76] = t19;
    $[77] = t22;
    $[78] = t37;
    $[79] = t43;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  let t45;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <DropdownMenuSeparator className="my-0" />;
    $[81] = t45;
  } else {
    t45 = $[81];
  }
  let t46;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <LogOut className="size-4" />;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== tMenu) {
    t47 = tMenu("signOut");
    $[83] = tMenu;
    $[84] = t47;
  } else {
    t47 = $[84];
  }
  let t48;
  if ($[85] !== t47) {
    t48 = <div className="p-1"><DropdownMenuItem onClick={_temp3} variant="destructive" className="gap-3 px-3 py-2.5">{t46}{t47}</DropdownMenuItem></div>;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  let t49;
  if ($[87] !== t13 || $[88] !== t3 || $[89] !== t4 || $[90] !== t44 || $[91] !== t48 || $[92] !== t6) {
    t49 = <DropdownMenuContent side={t3} align={t4} sideOffset={8} className={t6}>{t13}{t14}{t15}{t44}{t45}{t48}</DropdownMenuContent>;
    $[87] = t13;
    $[88] = t3;
    $[89] = t4;
    $[90] = t44;
    $[91] = t48;
    $[92] = t6;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  let t50;
  if ($[94] !== t2 || $[95] !== t49) {
    t50 = <DropdownMenu>{t2}{t49}</DropdownMenu>;
    $[94] = t2;
    $[95] = t49;
    $[96] = t50;
  } else {
    t50 = $[96];
  }
  const menu = t50;
  if (variant === "header") {
    return menu;
  }
  let t51;
  if ($[97] !== menu) {
    t51 = <SidebarMenu><SidebarMenuItem>{menu}</SidebarMenuItem></SidebarMenu>;
    $[97] = menu;
    $[98] = t51;
  } else {
    t51 = $[98];
  }
  return t51;
}
function _temp3() {
  return void signOut();
}
function _temp2(code) {
  return <DropdownMenuRadioItem key={code} value={code}>{LOCALE_LABELS[code]}</DropdownMenuRadioItem>;
}
function _temp(value) {
  return void setUserLocale(value);
}